// Renders a list of FieldDefs inside a react-hook-form <FormProvider>. Field
// names are dotted paths, so nested objects (an item in a list of objects)
// reuse the same components with a prefix.

import { Suspense, createContext, lazy, useContext, useState, type ReactNode } from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { api, type Item } from '../lib/api'
import type { FieldDef } from '../lib/types'
import { cairoOffset, joinIso, OFFSETS, splitIso } from '../lib/datetime'
import { getPath } from '../lib/formValues'
import MediaField from './MediaField'
import { Spinner } from '../components/ui'

// The markdown pipeline (unified) is most of a roadmap editor's weight; only load it there.
const RoadmapBodyField = lazy(() => import('./RoadmapBodyField'))

/** Which language the translatable inputs are showing. */
export const FormLangContext = createContext<'en' | 'ar'>('en')
export const useFormLang = () => useContext(FormLangContext)
/** The type's English-by-design fields (dotted paths, list indices left out): no "AR missing" chip on those. */
export const EnglishOnlyContext = createContext<string[]>([])

const join = (prefix: string, name: string) => (prefix ? `${prefix}.${name}` : name)

export function Fields({ fields, prefix = '' }: { fields: FieldDef[]; prefix?: string }) {
  return (
    <div className="space-y-5">
      {fields.map((f, i) => (
        <FieldView key={'name' in f ? f.name : `${f.kind}-${i}`} def={f} prefix={prefix} />
      ))}
    </div>
  )
}

function FieldView({ def, prefix }: { def: FieldDef; prefix: string }) {
  switch (def.kind) {
    case 'section':
      return (
        <fieldset className="card space-y-5 p-4">
          <legend className="px-1 text-sm font-semibold">{def.title}</legend>
          <Fields fields={def.fields} prefix={prefix} />
        </fieldset>
      )
    case 'l10n':
      return <L10nField name={join(prefix, def.name)} label={def.label} multiline={def.multiline} help={def.help} optional={def.optional} />
    case 'l10nList':
      return <L10nListField name={join(prefix, def.name)} label={def.label} multiline={def.multiline} help={def.help} />
    case 'stringList':
      return <StringListField name={join(prefix, def.name)} label={def.label} help={def.help} />
    case 'objects':
      return <ObjectsField def={def} name={join(prefix, def.name)} />
    case 'media':
      return <MediaField name={join(prefix, def.name)} label={def.label} folder={def.folder} help={def.help} />
    case 'datetime':
      return <DateTimeField name={join(prefix, def.name)} label={def.label} help={def.help} />
    case 'ref':
      return <RefField name={join(prefix, def.name)} label={def.label} to={def.to} help={def.help} />
    case 'roadmapBody':
      return <Suspense fallback={<Spinner />}><RoadmapBodyField /></Suspense>
    case 'boolean':
      return <BooleanField name={join(prefix, def.name)} label={def.label} help={def.help} />
    case 'select':
      return <SelectField name={join(prefix, def.name)} label={def.label} options={def.options} help={def.help} />
    case 'number':
      return <NumberField name={join(prefix, def.name)} label={def.label} step={def.step} help={def.help} />
    default:
      return <TextField name={join(prefix, def.name)} label={def.label} kind={def.kind} help={def.help} required={def.required} />
  }
}

/* ------------------------------------------------------------------ */
/* Building blocks                                                       */
/* ------------------------------------------------------------------ */

export function FieldShell({ name, label, help, children, side }: {
  name: string; label: string; help?: string; children: ReactNode; side?: ReactNode
}) {
  const { formState: { errors } } = useFormContext()
  const error = getPath(errors as Record<string, unknown>, name) as { message?: string } | undefined
  // An error on `title.en` should show under the `title` field too.
  const nested = !error?.message && (getPath(errors as Record<string, unknown>, `${name}.en`) as { message?: string } | undefined)
  const message = error?.message ?? (nested ? nested.message : undefined)
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className="label" htmlFor={name}>{label}</label>
        {side}
      </div>
      {children}
      {help && <p className="mt-1 text-xs text-faint">{help}</p>}
      {message && <p className="mt-1 text-xs text-danger">{message}</p>}
    </div>
  )
}

function TextField({ name, label, kind, help, required }: {
  name: string; label: string; kind: 'text' | 'url' | 'email' | 'date' | 'textarea'; help?: string; required?: boolean
}) {
  const { register } = useFormContext()
  return (
    <FieldShell name={name} label={required ? `${label} *` : label} help={help}>
      {kind === 'textarea'
        ? <textarea id={name} className="input min-h-24" {...register(name)} />
        : <input id={name} className="input" type={kind === 'text' ? 'text' : kind} {...register(name)} />}
    </FieldShell>
  )
}

function NumberField({ name, label, step, help }: { name: string; label: string; step?: number; help?: string }) {
  const { register } = useFormContext()
  return (
    <FieldShell name={name} label={label} help={help}>
      <input
        id={name}
        className="input max-w-40"
        type="number"
        step={step ?? 1}
        {...register(name, { setValueAs: (v) => (v === '' || v === null ? null : Number(v)) })}
      />
    </FieldShell>
  )
}

function BooleanField({ name, label, help }: { name: string; label: string; help?: string }) {
  const { register } = useFormContext()
  return (
    <div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" className="h-4 w-4 accent-[rgb(var(--accent))]" {...register(name)} />
        {label}
      </label>
      {help && <p className="mt-1 text-xs text-faint">{help}</p>}
    </div>
  )
}

function SelectField({ name, label, options, help }: { name: string; label: string; options: readonly string[]; help?: string }) {
  const { register } = useFormContext()
  return (
    <FieldShell name={name} label={label} help={help}>
      <select id={name} className="input max-w-64" {...register(name)}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </FieldShell>
  )
}

/* ---------- translatable text ---------- */

function L10nInput({ name, multiline }: { name: string; multiline?: boolean }) {
  const lang = useFormLang()
  const { register } = useFormContext()
  const english = useWatch({ name: `${name}.en` }) as string
  const field = register(`${name}.${lang}`)
  if (lang === 'en') {
    return multiline
      ? <textarea id={name} className="input min-h-24" {...field} />
      : <input id={name} className="input" {...field} />
  }
  return (
    <div>
      {english && <p className="mb-1 line-clamp-3 text-xs text-faint" title={english}>EN: {english}</p>}
      {multiline
        ? <textarea id={name} dir="rtl" lang="ar" className="input min-h-24" placeholder="الترجمة العربية" {...field} />
        : <input id={name} dir="rtl" lang="ar" className="input" placeholder="الترجمة العربية" {...field} />}
    </div>
  )
}

function MissingArabicDot({ name }: { name: string }) {
  const value = useWatch({ name }) as { en?: string; ar?: string } | undefined
  const englishOnly = useContext(EnglishOnlyContext)
  if (!value?.en || value.ar) return null
  if (englishOnly.includes(name.split('.').filter((p) => !/^\d+$/.test(p)).join('.'))) return null
  return <span className="chip border-warn/40 text-warn">AR missing</span>
}

function L10nField({ name, label, multiline, help, optional }: {
  name: string; label: string; multiline?: boolean; help?: string; optional?: boolean
}) {
  return (
    <FieldShell name={name} label={optional ? label : `${label} *`} help={help} side={<MissingArabicDot name={name} />}>
      <L10nInput name={name} multiline={multiline} />
    </FieldShell>
  )
}

function MoveButtons({ index, count, move, remove }: {
  index: number; count: number; move: (a: number, b: number) => void; remove: (i: number) => void
}) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      <button type="button" className="btn !px-1.5" disabled={index === 0} onClick={() => move(index, index - 1)} aria-label="Move up">
        <ArrowUp className="h-3.5 w-3.5" />
      </button>
      <button type="button" className="btn !px-1.5" disabled={index === count - 1} onClick={() => move(index, index + 1)} aria-label="Move down">
        <ArrowDown className="h-3.5 w-3.5" />
      </button>
      <button type="button" className="btn !px-1.5 text-danger" onClick={() => remove(index)} aria-label="Remove">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function L10nListField({ name, label, multiline, help }: { name: string; label: string; multiline?: boolean; help?: string }) {
  const { control } = useFormContext()
  const { fields, append, move, remove } = useFieldArray({ control, name })
  return (
    <FieldShell name={name} label={`${label} (${fields.length})`} help={help}>
      <div className="space-y-2">
        {fields.map((f, i) => (
          <div key={f.id} className="flex items-start gap-2">
            <span className="mt-2 w-5 shrink-0 text-end font-mono text-xs text-faint">{i + 1}</span>
            <div className="min-w-0 flex-1"><L10nInput name={`${name}.${i}`} multiline={multiline} /></div>
            <MoveButtons index={i} count={fields.length} move={move} remove={remove} />
          </div>
        ))}
        <button type="button" className="btn" onClick={() => append({ en: '', ar: '' })}>
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </FieldShell>
  )
}

function StringListField({ name, label, help }: { name: string; label: string; help?: string }) {
  return (
    <Controller
      name={name}
      render={({ field }) => (
        <FieldShell name={name} label={label} help={help}>
          <textarea
            id={name}
            className="input min-h-24 font-mono text-xs"
            value={((field.value as string[]) ?? []).join('\n')}
            onChange={(e) => field.onChange(e.target.value.split('\n'))}
            onBlur={() => field.onChange(((field.value as string[]) ?? []).map((s) => s.trim()).filter(Boolean))}
          />
        </FieldShell>
      )}
    />
  )
}

/* ---------- lists of objects (roadmap phase titles, …) ---------- */

function ObjectsField({ def, name }: { def: Extract<FieldDef, { kind: 'objects' }>; name: string }) {
  const { control, getValues } = useFormContext()
  const { fields, append, move, remove } = useFieldArray({ control, name })
  const [open, setOpen] = useState<Record<string, boolean>>({})
  return (
    <FieldShell name={name} label={`${def.label} (${fields.length})`} help={def.help}>
      <div className="space-y-2">
        {fields.map((f, i) => {
          const isOpen = open[f.id] ?? false
          return (
            <div key={f.id} className="rounded-lg border border-line">
              <div className="flex items-center gap-2 px-3 py-2">
                <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-start text-sm"
                        onClick={() => setOpen({ ...open, [f.id]: !isOpen })}>
                  {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                  <span className="truncate">{def.title(getValues(`${name}.${i}`), i) || <em className="text-faint">untitled</em>}</span>
                </button>
                <MoveButtons index={i} count={fields.length} move={move} remove={remove} />
              </div>
              {isOpen && <div className="border-t border-line p-3"><Fields fields={def.fields} prefix={`${name}.${i}`} /></div>}
            </div>
          )
        })}
        <button type="button" className="btn" onClick={() => append(def.blank())}>
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </FieldShell>
  )
}

/* ---------- date-time with offset ---------- */

function DateTimeField({ name, label, help }: { name: string; label: string; help?: string }) {
  return (
    <Controller
      name={name}
      render={({ field }) => {
        const { local, offset } = splitIso(field.value as string)
        const set = (l: string, o: string) => field.onChange(joinIso(l, o))
        return (
          <FieldShell name={name} label={label} help={help}>
            <div className="flex flex-wrap gap-2">
              <input
                id={name}
                type="datetime-local"
                className="input max-w-64"
                value={local}
                // A new time picks Cairo's offset for that date (DST-aware).
                onChange={(e) => set(e.target.value, e.target.value ? cairoOffset(e.target.value) : offset)}
              />
              <select className="input max-w-32" value={offset} onChange={(e) => set(local, e.target.value)} aria-label="UTC offset">
                {!offset && <option value="">offset</option>}
                {OFFSETS.map((o) => <option key={o} value={o}>UTC{o}</option>)}
              </select>
            </div>
          </FieldShell>
        )
      }}
    />
  )
}

/* ---------- reference to another type's item ---------- */

const REF_LABEL = {
  partners: (i: Item) => String(i.name),
}

function RefField({ name, label, to, help }: { name: string; label: string; to: keyof typeof REF_LABEL; help?: string }) {
  const { register } = useFormContext()
  const items = useQuery({ queryKey: ['content', to], queryFn: () => api.get<{ items: Item[] }>(`/content/${to}`) })
  return (
    <FieldShell name={name} label={label} help={help}>
      <select id={name} className="input max-w-64" {...register(name)}>
        <option value="">— none —</option>
        {items.data?.items.map((p) => <option key={p.slug} value={p.slug}>{REF_LABEL[to](p)}</option>)}
      </select>
    </FieldShell>
  )
}
